import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, FormControl, FormHelperText, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import PregnantWomanIcon from '@material-ui/icons/PregnantWoman';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { AppSelect, AppToggleButton, AppToggleButtonGroup, AppSlider } from './InputComponents';
import { MobileButton } from "../../../components/MobileButton";
import { arrayOfNumbers } from '../../../services/util.service';
import useStyles from "./Pregnancies.style";
import multiItemQuestion from './MultiItemQuestion.component';
import { useTranslation } from 'react-i18next';

const Pregnancies = props => {
	const classes = useStyles();
	const { t } = useTranslation();

	const [autoUpdate, setAutoUpdate] = useState(false);

	let birthYear = new Date(props.dateOfBirth).getFullYear();
	let firstPeriodAge = parseInt(props.firstPeriodAge);
	let firstPeriodYear = firstPeriodAge ? birthYear + firstPeriodAge : null;
	let currentYear = new Date(Date.now()).getFullYear();
	let pregnancyYears = firstPeriodYear
		? [...arrayOfNumbers(firstPeriodYear, currentYear - firstPeriodYear + 1)]
		: [];

	useEffect(() => {
		if (props.haveBeenPregnant === 'No') {
			if (props.direction === 'backward')
				props.back();
			else
				props.next();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		props.validator.purgeFields();
		setAutoUpdate(!autoUpdate);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.items]);

	const handleOutcomeChange = (i, outcome) => e => {
		props.onChange(i, 'isProviderView', 'true')();
		if (outcome !== 'Live Birth') {
			props.onChange(i, 'liveBirthQty', 0)();
			props.onChange(i, 'stillbirthQty', 0)();
			props.onChange(i, 'placedAdoptionQty', 0)();
		};

		props.onChange(i, 'outcome', outcome)(e);
	};

	return (
		<Box className={classes.root}>
			{/* Header */}
			<Box><PregnantWomanIcon className={classes.textIcon} /></Box>
			<Typography variant="h4" className={classes.title}>Please give us information about each pregnancy</Typography>
			<Typography className={classes.subTitle}>
			</Typography>
			{props.error && !props.validator.allValid() &&
				<Typography color="error">
					Please review your answers
				</Typography>
			}
			{props.items.map((pregnancy, i) =>
				<Box className={classes.pregnancy} key={i}>
					<Box className={classes.pregnancyHeader}>
						<span>{t('common.substantive.pregnancy')} #{i + 1}</span>
						<IconButton aria-label="delete" color="inherit" onClick={props.onRemove(i)}>
							<DeleteIcon />
						</IconButton>
					</Box>
					<Box className={classes.pregnancyBody}>
						<Box className={classes.inputSection}>
							<Typography variant="h5">Year of Pregnancy
							{
                             <span className={props.error && !props.validator.fieldValid(`pregnancy_#${i + 1}_year`) ? `${classes.asteriskError}` : `${classes.asteriskNormal}`} >*</span>
                            }
								</Typography>
							<AppSelect
								id={`pregnancy${i + 1}`}
								label={`${t('common.substantive.year')}*`}
								value={pregnancy.year}
								items={pregnancyYears}
								onChange={props.onChange(i, 'year')}
								fullWidth
								error={props.error && !props.validator.fieldValid(`pregnancy_#${i + 1}_year`)}
								helperText={props.validator.message(`pregnancy_#${i + 1}_year`, pregnancy.year, 'required')}
							/>
						</Box>
						<Box className={classes.inputSection}>
							<Typography variant="h5">
								Intention
								{
                             <span className={props.error && !props.validator.fieldValid(`pregnancy_#${i + 1}_intention`) ? `${classes.asteriskError}` : `${classes.asteriskNormal}`} >*</span>
                            }
								</Typography>
							<FormControl error={props.error && !props.validator.fieldValid(`pregnancy_#${i + 1}_intention`)}>
								<AppToggleButtonGroup>
									{['Intended', 'Unintended'].map(intention =>
										<AppToggleButton
											key={intention}
											selected={pregnancy.intention === intention}
											onChange={props.onChange(i, 'intention', intention)}
											name={intention}
											label={intention}
										/>
									)}
								</AppToggleButtonGroup>
								<FormHelperText>
									{props.validator.message(`pregnancy_#${i + 1}_intention`, pregnancy.intention, 'required')}
								</FormHelperText>
							</FormControl>
						</Box>
						<Box className={classes.inputSection}>
							<Typography variant="h5">
							
								Outcome
								{
                             <span className={props.error && !props.validator.fieldValid(`pregnancy_#${i + 1}_outcome`) ? `${classes.asteriskError}` : `${classes.asteriskNormal}`} >*</span>
                            }
								</Typography>
							<FormControl error={props.error && !props.validator.fieldValid(`pregnancy_#${i + 1}_outcome`)}>
								<AppToggleButtonGroup>
									{['Live Birth', 'Miscarriage', 'Ectopic or Tubal', 'Abortion'].map(outcome =>
										<AppToggleButton
											key={outcome}
											selected={pregnancy.outcome === outcome}
											onChange={handleOutcomeChange(i, outcome)}
											name={outcome}
											label={outcome}
										/>
									)}
								</AppToggleButtonGroup>
								<FormHelperText>
									{props.validator.message(`pregnancy_#${i + 1}_outcome`, pregnancy.outcome, 'required')}
								</FormHelperText>
							</FormControl>
						</Box>
						{/* If there is any Miscarrage or Abortion outcome we have ask the patient to hiding his information for the provider or not */}
						<Box display={pregnancy.outcome === 'Abortion' ? 'block' : 'none'}>
							<Typography variant="h5">Do you want to display this information to provider?</Typography>
							<FormControl
								component="fieldset"
								error={props.error && !props.validator.fieldValid(`pregnancy_#${i + 1}_isProviderView`)}
							>
								<RadioGroup row
									value={pregnancy.isProviderView}
									onChange={props.onChange(i, 'isProviderView')}
								>
									<FormControlLabel value='true' control={<Radio color="primary" />} label="Yes" />
									<FormControlLabel value='false' control={<Radio color="primary" />} label="No" />
								</RadioGroup>
								<FormHelperText error style={{ textAlign: "center" }}>
									{props.validator.message(`pregnancy_#${i + 1}_isProviderView`, pregnancy.isProviderView, 'required')}
								</FormHelperText>
							</FormControl>
						</Box>

						<Box display={pregnancy.outcome === 'Live Birth' ? 'block' : 'none'}>
							<Box className={classes.inputSection}>
								<Typography variant="h5">Number of Live Births</Typography>
								<AppSlider
									valueLabelDisplay="on"
									min={0}
									max={10}
									value={pregnancy.liveBirthQty}
									onChange={(e, newValue) => props.onChange(i, 'liveBirthQty', newValue)(e)}
									marks={[
										{
											value: 0,
											label: '0',
										},
										{
											value: 10,
											label: '10',
										},
									]}
								/>
							</Box>
							<Box className={classes.inputSection}>
								<Typography variant="h5">Number of Stillbirths</Typography>
								<AppSlider
									valueLabelDisplay="on"
									min={0}
									max={10}
									value={pregnancy.stillbirthQty}
									onChange={(e, newValue) => props.onChange(i, 'stillbirthQty', newValue)(e)}
									marks={[
										{
											value: 0,
											label: '0',
										},
										{
											value: 10,
											label: '10',
										},
									]}
								/>
							</Box>
							<Box className={classes.inputSection}>
								<Typography variant="h5">Number of Placed for adoption</Typography>
								<AppSlider
									valueLabelDisplay="on"
									min={0}
									max={10}
									value={pregnancy.placedAdoptionQty}
									onChange={(e, newValue) => props.onChange(i, 'placedAdoptionQty', newValue)(e)}
									marks={[
										{
											value: 0,
											label: '0',
										},
										{
											value: 10,
											label: '10',
										},
									]}
								/>
							</Box>
						</Box>
					</Box>
				</Box>
			)
			}
			<MobileButton
				onClick={props.onAdd}
				color="primary"
				label="Add Pregnancy"
				endIcon={<AddIcon />}
			/>
		</Box >
	);
};

export default multiItemQuestion(
	Pregnancies,
	{
		year: '',
		intention: '',
		outcome: '',
		isProviderView: 'true',
		liveBirthQty: 0,
		stillbirthQty: 0,
		placedAdoptionQty: 0
	});
